// frontend/src/pages/PromosPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Módulo de Promociones
//   · Promo 5+1   — compra inmediata: lleva 5, la 6ta es gratis
//   · Tarjeta 7+1 — acumulación: 7 sellos → 1 unidad gratis
// Solo el dueño/admin puede operar este módulo.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { promosAPI } from '../services/api';
import api from '../services/api';
import Toast from '../components/Toast';
import './PromosPage.css';

// ── Componente: Barra de sellos visual ───────────────────────────────────────
const BarraSellos = ({ sellos, meta = 7 }) => {
    return (
        <div className="promos-sello-barra">
            {Array.from({ length: meta }, (_, i) => (
                <div
                    key={i}
                    className={`promos-sello-circulo ${i < sellos ? 'activo' : ''} ${i === meta - 1 ? 'ultimo' : ''}`}
                    title={`Sello ${i + 1}`}
                >
                    {i < sellos ? '⭐' : (i === meta - 1 ? '🎁' : '')}
                </div>
            ))}
        </div>
    );
};

// ── Componente: Badge de estado de tarjeta ───────────────────────────────────
const BadgeTarjeta = ({ tarjeta }) => {
    if (!tarjeta) return <span className="promos-badge sin-tarjeta">Sin tarjeta</span>;
    if (tarjeta.completada && !tarjeta.canjeada) return <span className="promos-badge canje-listo">🎁 Canje listo</span>;
    if (tarjeta.completada && tarjeta.canjeada) return <span className="promos-badge canjeada">Canjeada</span>;
    return <span className="promos-badge activa">{tarjeta.sellos}/7 sellos</span>;
};

// ── Componente principal ─────────────────────────────────────────────────────
const PromosPage = () => {
    const [tab, setTab] = useState('tarjeta');
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);

    const [busqueda, setBusqueda] = useState('');
    const [clientes, setClientes] = useState([]);
    const [buscandoClientes, setBuscandoClientes] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [creandoCliente, setCreandoCliente] = useState(false);
    const [cantidadPersonalizada, setCantidadPersonalizada] = useState(1);

    const [tarjetaInfo, setTarjetaInfo] = useState(null);
    const [loadingTarjeta, setLoadingTarjeta] = useState(false);

    const [clientesConSellos, setClientesConSellos] = useState([]);
    const [loadingLista, setLoadingLista] = useState(false);

    const [resumen, setResumen] = useState(null);

    const showToast = (message, type = 'success') => setToast({ message, type });

    const cargarResumen = useCallback(async () => {
        try {
            const res = await promosAPI.getResumen();
            setResumen(res.data.data);
        } catch { }
    }, []);

    const cargarLista = useCallback(async () => {
        setLoadingLista(true);
        try {
            const res = await promosAPI.getClientesConSellos();
            setClientesConSellos(res.data.data.clientes || []);
        } catch {
            showToast('Error al cargar la lista', 'error');
        } finally {
            setLoadingLista(false);
        }
    }, []);

    useEffect(() => { cargarResumen(); }, [cargarResumen]);
    useEffect(() => { if (tab === 'lista') cargarLista(); }, [tab, cargarLista]);

    useEffect(() => {
        if (busqueda.trim().length < 2) {
            setClientes([]);
            setShowDropdown(false);
            return;
        }
        const timer = setTimeout(async () => {
            setBuscandoClientes(true);
            try {
                const res = await api.get(`/customers?search=${encodeURIComponent(busqueda)}`);
                const lista = res.data.data?.customers || res.data.customers || [];
                setClientes(lista.slice(0, 8));
                setShowDropdown(true);
            } catch {
                setClientes([]);
            } finally {
                setBuscandoClientes(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [busqueda]);

    const seleccionarCliente = async (cliente) => {
        setClienteSeleccionado(cliente);
        setBusqueda(cliente.name);
        setShowDropdown(false);
        setLoadingTarjeta(true);
        try {
            const res = await promosAPI.getTarjeta(cliente.id);
            setTarjetaInfo(res.data.data);
        } catch {
            setTarjetaInfo({ cliente, tarjeta: null, historial: [] });
        } finally {
            setLoadingTarjeta(false);
        }
    };

    // Crear cliente rápido al instante solo con el nombre
    const crearYSeleccionarCliente = async () => {
        const nombre = busqueda.trim();
        if (!nombre) return showToast('Escribe el nombre del cliente.', 'error');
        setCreandoCliente(true);
        try {
            const res = await api.post('/customers', { name: nombre });
            const nuevo = res.data.data?.customer || res.data.customer || { id: res.data.data?.id || res.data.id, name: nombre };
            showToast(`Cliente "${nombre}" registrado con éxito.`);
            await seleccionarCliente(nuevo);
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al registrar el cliente', 'error');
        } finally {
            setCreandoCliente(false);
        }
    };

    const registrarSellos = async (cantidad) => {
        if (!clienteSeleccionado) return showToast('Selecciona un cliente primero.', 'error');
        setLoading(true);
        try {
            const res = await promosAPI.agregarSellos(clienteSeleccionado.id, cantidad);
            showToast(res.data.message);
            const updated = await promosAPI.getTarjeta(clienteSeleccionado.id);
            setTarjetaInfo(updated.data.data);
            cargarResumen();
            if (tab === 'lista') cargarLista();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al registrar sellos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const ajustarSellosDirecto = async (nuevosSellos) => {
        if (!clienteSeleccionado) return;
        if (nuevosSellos < 0 || nuevosSellos > 7) return;
        setLoading(true);
        try {
            const res = await promosAPI.ajustarSellos(clienteSeleccionado.id, nuevosSellos);
            showToast(res.data.message);
            const updated = await promosAPI.getTarjeta(clienteSeleccionado.id);
            setTarjetaInfo(updated.data.data);
            cargarResumen();
            if (tab === 'lista') cargarLista();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al ajustar sellos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const canjearRegalo = async () => {
        if (!clienteSeleccionado) return;
        setLoading(true);
        try {
            const res = await promosAPI.canjear(clienteSeleccionado.id);
            showToast(res.data.message);
            const updated = await promosAPI.getTarjeta(clienteSeleccionado.id);
            setTarjetaInfo(updated.data.data);
            cargarResumen();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al canjear', 'error');
        } finally {
            setLoading(false);
        }
    };

    const deshacerUltimo = async () => {
        if (!clienteSeleccionado) return;
        setLoading(true);
        try {
            const res = await promosAPI.deshacer(clienteSeleccionado.id);
            showToast(res.data.message, 'info');
            const updated = await promosAPI.getTarjeta(clienteSeleccionado.id);
            setTarjetaInfo(updated.data.data);
            cargarResumen();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al deshacer', 'error');
        } finally {
            setLoading(false);
        }
    };

    const registrarPromo5 = async () => {
        if (!clienteSeleccionado) return showToast('Selecciona un cliente primero.', 'error');
        setLoading(true);
        try {
            const res = await promosAPI.promo5mas1(clienteSeleccionado.id);
            showToast(res.data.message);
            const updated = await promosAPI.getTarjeta(clienteSeleccionado.id);
            setTarjetaInfo(updated.data.data);
            cargarResumen();
        } catch (err) {
            showToast(err.response?.data?.message || 'Error al registrar la promo 5+1', 'error');
        } finally {
            setLoading(false);
        }
    };

    const irATarjetaDesdeLista = (cliente) => {
        setTab('tarjeta');
        seleccionarCliente(cliente);
    };

    const fmtFecha = (iso) => {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="promos-page">
            <div className="promos-header">
                <div className="promos-header-left">
                    <div className="promos-title-icon">🎁</div>
                    <div>
                        <h1 className="promos-title">Promociones</h1>
                        <p className="promos-subtitle">Gestiona la promo 5+1 y la tarjeta de sellos virtual 7+1</p>
                    </div>
                </div>
                {resumen && (
                    <div className="promos-stats-row">
                        <div className="promos-stat-chip canjes">
                            <span className="promos-stat-num">{resumen.canjes_pendientes || 0}</span>
                            <span className="promos-stat-lbl">Canjes listos</span>
                        </div>
                        <div className="promos-stat-chip activas">
                            <span className="promos-stat-num">{resumen.tarjetas_activas || 0}</span>
                            <span className="promos-stat-lbl">Tarjetas activas</span>
                        </div>
                        <div className="promos-stat-chip entregados">
                            <span className="promos-stat-num">{resumen.total_canjes_entregados || 0}</span>
                            <span className="promos-stat-lbl">Canjes entregados</span>
                        </div>
                        <div className="promos-stat-chip promo5">
                            <span className="promos-stat-num">{resumen.total_promo5mas1 || 0}</span>
                            <span className="promos-stat-lbl">Promos 5+1</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="promos-tabs">
                <button className={`promos-tab ${tab === 'tarjeta' ? 'activo' : ''}`} onClick={() => setTab('tarjeta')}>
                    📇 Tarjeta 7+1
                </button>
                <button className={`promos-tab ${tab === 'promo5' ? 'activo' : ''}`} onClick={() => setTab('promo5')}>
                    ⚡ Promo 5+1
                </button>
                <button className={`promos-tab ${tab === 'lista' ? 'activo' : ''}`} onClick={() => setTab('lista')}>
                    👥 Todos los clientes
                </button>
            </div>

            {/* TAB: Tarjeta 7+1 */}
            {tab === 'tarjeta' && (
                <div className="promos-content">
                    <div className="promos-card promos-buscador-card">
                        <h3 className="promos-card-title">👤 Cliente / Registro Rápido</h3>
                        <p className="promos-buscador-sub">
                            Escribe el nombre del cliente. Si ya existe, selecciónalo; si es nuevo, regístralo al instante con un solo clic.
                        </p>
                        <div className="promos-search-row">
                            <div className="promos-search-wrap">
                                <input
                                    className="promos-search-input"
                                    type="text"
                                    placeholder="Nombre del cliente..."
                                    value={busqueda}
                                    onChange={e => {
                                        setBusqueda(e.target.value);
                                        if (clienteSeleccionado && e.target.value !== clienteSeleccionado.name) {
                                            setClienteSeleccionado(null);
                                            setTarjetaInfo(null);
                                        }
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && busqueda.trim()) {
                                            if (clientes.length === 1) {
                                                seleccionarCliente(clientes[0]);
                                            } else if (clientes.length === 0) {
                                                crearYSeleccionarCliente();
                                            }
                                        }
                                    }}
                                    autoComplete="off"
                                />
                                {buscandoClientes && <span className="promos-spinner" />}
                                {showDropdown && (
                                    <div className="promos-dropdown">
                                        {clientes.map(c => (
                                            <button key={c.id} className="promos-dropdown-item" onClick={() => seleccionarCliente(c)}>
                                                <span className="promos-dropdown-name">👤 {c.name}</span>
                                                {c.phone && <span className="promos-dropdown-phone">📱 {c.phone}</span>}
                                            </button>
                                        ))}
                                        {busqueda.trim().length >= 2 && !clientes.some(c => c.name.toLowerCase() === busqueda.trim().toLowerCase()) && (
                                            <button className="promos-dropdown-item nuevo" onClick={crearYSeleccionarCliente} disabled={creandoCliente}>
                                                <span>➕ Registrar nuevo: <strong>"{busqueda.trim()}"</strong></span>
                                                <span className="promos-tag-nuevo">Nuevo</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                className="promos-btn-crear-directo"
                                onClick={crearYSeleccionarCliente}
                                disabled={creandoCliente || !busqueda.trim()}
                                title="Crear cliente solo con este nombre"
                            >
                                {creandoCliente ? 'Guardando...' : '➕ Registrar Cliente'}
                            </button>
                        </div>
                    </div>

                    {loadingTarjeta && <div className="promos-loading">Cargando tarjeta...</div>}

                    {clienteSeleccionado && !loadingTarjeta && (
                        <div className="promos-grid-2col">
                            <div className="promos-card promos-tarjeta-card">
                                <div className="promos-tarjeta-header">
                                    <div className="promos-cliente-avatar">
                                        {clienteSeleccionado.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h2 className="promos-cliente-nombre">{clienteSeleccionado.name}</h2>
                                        {clienteSeleccionado.phone && (
                                            <p className="promos-cliente-phone">📱 {clienteSeleccionado.phone}</p>
                                        )}
                                    </div>
                                    <BadgeTarjeta tarjeta={tarjetaInfo?.tarjeta} />
                                </div>

                                <div className="promos-sellos-section">
                                    <div className="promos-sellos-titulo">
                                        <span>Tarjeta virtual 7+1</span>
                                        <span className="promos-sellos-contador">
                                            {tarjetaInfo?.tarjeta?.sellos || 0} / 7 sellos
                                        </span>
                                    </div>
                                    <BarraSellos sellos={tarjetaInfo?.tarjeta?.sellos || 0} />
                                    <p className="promos-sellos-hint">
                                        Al completar 7 sellos, la siguiente unidad es <strong>gratis</strong> 🎁
                                    </p>
                                </div>

                                {/* Ajustador rápido de sellos (- / +) */}
                                <div className="promos-editor-sellos">
                                    <span className="promos-editor-lbl">Editar sellos acumulados:</span>
                                    <div className="promos-stepper">
                                        <button
                                            className="promos-step-btn"
                                            onClick={() => ajustarSellosDirecto(Math.max(0, (tarjetaInfo?.tarjeta?.sellos || 0) - 1))}
                                            disabled={loading || (tarjetaInfo?.tarjeta?.sellos || 0) <= 0}
                                            title="Restar 1 sello"
                                        >
                                            −
                                        </button>
                                        <span className="promos-step-val">{tarjetaInfo?.tarjeta?.sellos || 0} / 7</span>
                                        <button
                                            className="promos-step-btn"
                                            onClick={() => ajustarSellosDirecto(Math.min(7, (tarjetaInfo?.tarjeta?.sellos || 0) + 1))}
                                            disabled={loading || (tarjetaInfo?.tarjeta?.sellos || 0) >= 7}
                                            title="Sumar 1 sello"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                <div className="promos-acciones">
                                    <p className="promos-acciones-titulo">Sumar sellos por compra:</p>
                                    <div className="promos-botones-sellos">
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <button
                                                key={n}
                                                className="promos-btn-sello"
                                                onClick={() => registrarSellos(n)}
                                                disabled={loading || (tarjetaInfo?.tarjeta?.completada && !tarjetaInfo?.tarjeta?.canjeada)}
                                            >
                                                +{n}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Entrada personalizada de cuántos más */}
                                    <div className="promos-custom-add">
                                        <span className="promos-custom-lbl">Otro monto:</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            className="promos-input-num"
                                            value={cantidadPersonalizada}
                                            onChange={e => setCantidadPersonalizada(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        />
                                        <button
                                            className="promos-btn-add-custom"
                                            onClick={() => registrarSellos(cantidadPersonalizada)}
                                            disabled={loading}
                                        >
                                            +{cantidadPersonalizada} sellos
                                        </button>
                                    </div>

                                    {tarjetaInfo?.tarjeta?.completada && !tarjetaInfo?.tarjeta?.canjeada && (
                                        <button className="promos-btn-canje" onClick={canjearRegalo} disabled={loading}>
                                            🎁 Entregar unidad gratis (Canjear)
                                        </button>
                                    )}

                                    <button
                                        className="promos-btn-deshacer"
                                        onClick={deshacerUltimo}
                                        disabled={loading || !tarjetaInfo?.historial?.length}
                                    >
                                        ↩ Deshacer último movimiento
                                    </button>
                                </div>
                            </div>

                            <div className="promos-card promos-historial-card">
                                <h3 className="promos-card-title">📋 Historial de movimientos</h3>
                                {!tarjetaInfo?.historial?.length ? (
                                    <div className="promos-historial-vacio">Sin movimientos aún.</div>
                                ) : (
                                    <div className="promos-historial-lista">
                                        {tarjetaInfo.historial.map((mov, i) => (
                                            <div key={mov.id || i} className={`promos-historial-item tipo-${mov.tipo}`}>
                                                <div className="promos-historial-icono">
                                                    {mov.tipo === 'sello' && '⭐'}
                                                    {mov.tipo === 'canje' && '🎁'}
                                                    {mov.tipo === 'promo5mas1' && '⚡'}
                                                    {mov.tipo === 'deshacer' && '↩'}
                                                </div>
                                                <div className="promos-historial-info">
                                                    <span className="promos-historial-desc">{mov.descripcion}</span>
                                                    <span className="promos-historial-fecha">{fmtFecha(mov.created_at)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!clienteSeleccionado && !loadingTarjeta && (
                        <div className="promos-empty-state">
                            <div className="promos-empty-icon">📇</div>
                            <h3>Busca un cliente para ver o registrar su tarjeta de sellos</h3>
                            <p>Escribe al menos 2 letras del nombre del cliente en el buscador de arriba.</p>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Promo 5+1 */}
            {tab === 'promo5' && (
                <div className="promos-content">
                    <div className="promos-grid-2col">
                        <div className="promos-card promos-info-card">
                            <div className="promos-info-icon">⚡</div>
                            <h2 className="promos-info-titulo">Promo 5+1 (inmediata)</h2>
                            <p className="promos-info-desc">
                                Si el cliente lleva <strong>5 unidades</strong> en este momento,
                                la <strong>6ta es gratis</strong>.
                            </p>
                            <div className="promos-regla-lista">
                                <div className="promos-regla-item">✅ Solo aplica cuando lleva 5+ unidades de golpe</div>
                                <div className="promos-regla-item">🚫 No suma sellos a la tarjeta virtual</div>
                                <div className="promos-regla-item">📝 Solo registra el evento para tu historial</div>
                                <div className="promos-regla-item">💰 Ganancia ~4.83 soles/unidad (descuento 16.7%)</div>
                            </div>
                            <div className="promos-ejemplo-box">
                                <strong>Ejemplo:</strong> 5 unidades a S/5.80 c/u = S/29.00 + 1 gratis.
                                Ganancia real: <strong>S/24.15</strong>
                            </div>
                        </div>

                        <div className="promos-card">
                            <h3 className="promos-card-title">Registrar Promo 5+1</h3>
                            <div className="promos-search-row" style={{ marginBottom: '20px' }}>
                                <div className="promos-search-wrap" style={{ flex: 1 }}>
                                    <label className="promos-label">Cliente</label>
                                    <input
                                        className="promos-search-input"
                                        type="text"
                                        placeholder="Escribe el nombre del cliente..."
                                        value={busqueda}
                                        onChange={e => {
                                            setBusqueda(e.target.value);
                                            if (clienteSeleccionado && e.target.value !== clienteSeleccionado.name) {
                                                setClienteSeleccionado(null);
                                            }
                                        }}
                                        autoComplete="off"
                                    />
                                    {buscandoClientes && <span className="promos-spinner" />}
                                    {showDropdown && (
                                        <div className="promos-dropdown">
                                            {clientes.map(c => (
                                                <button key={c.id} className="promos-dropdown-item"
                                                    onClick={() => { setClienteSeleccionado(c); setBusqueda(c.name); setShowDropdown(false); }}>
                                                    <span className="promos-dropdown-name">👤 {c.name}</span>
                                                    {c.phone && <span className="promos-dropdown-phone">📱 {c.phone}</span>}
                                                </button>
                                            ))}
                                            {busqueda.trim().length >= 2 && !clientes.some(c => c.name.toLowerCase() === busqueda.trim().toLowerCase()) && (
                                                <button className="promos-dropdown-item nuevo" onClick={crearYSeleccionarCliente} disabled={creandoCliente}>
                                                    <span>➕ Registrar nuevo: <strong>"{busqueda.trim()}"</strong></span>
                                                    <span className="promos-tag-nuevo">Nuevo</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="promos-btn-crear-directo"
                                    style={{ alignSelf: 'flex-end', height: '44px' }}
                                    onClick={crearYSeleccionarCliente}
                                    disabled={creandoCliente || !busqueda.trim()}
                                >
                                    {creandoCliente ? '...' : '➕ Registrar'}
                                </button>
                            </div>

                            {clienteSeleccionado && (
                                <div className="promos-cliente-seleccionado">
                                    <div className="promos-cliente-avatar small">
                                        {clienteSeleccionado.name.charAt(0).toUpperCase()}
                                    </div>
                                    <span>{clienteSeleccionado.name}</span>
                                </div>
                            )}

                            <button className="promos-btn-promo5" onClick={registrarPromo5} disabled={loading || !clienteSeleccionado}>
                                ⚡ Registrar Promo 5+1
                            </button>
                            {!clienteSeleccionado && (
                                <p className="promos-hint-gris">Selecciona un cliente para continuar.</p>
                            )}
                        </div>
                    </div>

                    {clienteSeleccionado && tarjetaInfo?.historial?.filter(m => m.tipo === 'promo5mas1').length > 0 && (
                        <div className="promos-card" style={{ marginTop: '16px' }}>
                            <h3 className="promos-card-title">Historial Promo 5+1 — {clienteSeleccionado.name}</h3>
                            <div className="promos-historial-lista">
                                {tarjetaInfo.historial.filter(m => m.tipo === 'promo5mas1').map((mov, i) => (
                                    <div key={mov.id || i} className="promos-historial-item tipo-promo5mas1">
                                        <div className="promos-historial-icono">⚡</div>
                                        <div className="promos-historial-info">
                                            <span className="promos-historial-desc">{mov.descripcion}</span>
                                            <span className="promos-historial-fecha">{fmtFecha(mov.created_at)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: Lista */}
            {tab === 'lista' && (
                <div className="promos-content">
                    {loadingLista ? (
                        <div className="promos-loading">Cargando clientes...</div>
                    ) : clientesConSellos.length === 0 ? (
                        <div className="promos-empty-state">
                            <div className="promos-empty-icon">👥</div>
                            <h3>Ningún cliente tiene tarjeta de sellos aún</h3>
                            <p>Cuando registres sellos a un cliente, aparecerá aquí.</p>
                        </div>
                    ) : (
                        <div className="promos-card">
                            <div className="promos-lista-header">
                                <h3 className="promos-card-title">👥 Clientes con tarjeta de sellos</h3>
                                <button className="promos-btn-refresh" onClick={cargarLista}>🔄 Actualizar</button>
                            </div>
                            {/* Vista tabla en desktop */}
                            <div className="promos-tabla-wrap desktop-only">
                                <table className="promos-tabla">
                                    <thead>
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Progreso</th>
                                            <th>Estado</th>
                                            <th>Última actividad</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientesConSellos.map(c => (
                                            <tr key={c.id} className={c.completada && !c.canjeada ? 'fila-canje-listo' : ''}>
                                                <td>
                                                    <div className="promos-tabla-cliente">
                                                        <div className="promos-cliente-avatar mini">{c.name.charAt(0).toUpperCase()}</div>
                                                        <div>
                                                            <span className="promos-tabla-nombre">{c.name}</span>
                                                            {c.phone && <span className="promos-tabla-phone">📱 {c.phone}</span>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="promos-tabla-sellos">
                                                        <BarraSellos sellos={c.sellos} />
                                                        <span className="promos-tabla-sellos-num">{c.sellos}/7</span>
                                                    </div>
                                                </td>
                                                <td><BadgeTarjeta tarjeta={c} /></td>
                                                <td className="promos-tabla-fecha">{fmtFecha(c.ultima_actividad)}</td>
                                                <td>
                                                    <button className="promos-btn-ver" onClick={() => irATarjetaDesdeLista(c)}>
                                                        Ver tarjeta
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Vista tarjetas limpias en móvil */}
                            <div className="promos-clientes-mobile-list mobile-only">
                                {clientesConSellos.map(c => (
                                    <div key={c.id} className={`promos-cliente-card-mob ${c.completada && !c.canjeada ? 'canje-listo' : ''}`}>
                                        <div className="promos-card-mob-top">
                                            <div className="promos-tabla-cliente">
                                                <div className="promos-cliente-avatar mini">{c.name.charAt(0).toUpperCase()}</div>
                                                <div>
                                                    <span className="promos-tabla-nombre">{c.name}</span>
                                                    {c.phone && <span className="promos-tabla-phone">📱 {c.phone}</span>}
                                                </div>
                                            </div>
                                            <BadgeTarjeta tarjeta={c} />
                                        </div>

                                        <div className="promos-card-mob-sellos">
                                            <BarraSellos sellos={c.sellos} />
                                            <span className="promos-tabla-sellos-num">{c.sellos}/7 sellos</span>
                                        </div>

                                        <div className="promos-card-mob-footer">
                                            <span className="promos-card-mob-fecha">🕒 {fmtFecha(c.ultima_actividad)}</span>
                                            <button className="promos-btn-ver" onClick={() => irATarjetaDesdeLista(c)}>
                                                Abrir tarjeta →
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default PromosPage;
